import {
  Filter,
  FilterValue,
  InputType,
  RowVisibility,
} from "../../types/filter";
import { TrackingProps } from "../../types/component";
import { useTranslation } from "@coworker/locales";
import FixaButton from "../../../wrappers/FixaButton/FixaButton";
import FixaFiltersIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/FiltersIconPath";
import { useChangedFiltersCount } from "../../utils/useChangedFiltersCount";
import { FilterRowScrollPanel } from "../FilterRowScrollPanel";
import { InputSelectPillDesktop } from "./InputSelectPillDesktop";
import { InputMultiSelectPillDesktop } from "./InputMultiSelectPillDesktop";
import { PillRadioButton } from "../FilterPill/PillRadioButton";
import { InputGroupPillDesktop } from "./InputGroupPillDesktop";
import { InputSelectPillMobile } from "../FilterRowMobile/InputSelectPillMobile";
import { InputMultiSelectPillMobile } from "../FilterRowMobile/InputMultiSelectPillMobile";
import { InputGroupPillMobile } from "../FilterRowMobile/InputGroupPillMobile";
import trackerHelper from "@coworker/app/src/helpers/tracker";
import { PillCheckbox } from "../FilterPill/PillCheckbox";

interface FilterRowDesktopProps {
  filters: Filter[];
  filterValues: FilterValue[];
  onTriggerFilter: (filterId: string) => void;
  setFilterValues: (filterValues: FilterValue[]) => void;
  tracking: TrackingProps;
}

export const FilterRowDesktop = ({
  filters,
  filterValues,
  setFilterValues,
  onTriggerFilter,
  tracking,
}: FilterRowDesktopProps) => {
  const { t } = useTranslation();
  const changedFilterCount = useChangedFiltersCount(filters, filterValues);

  return (
    <FilterRowScrollPanel>
      <FixaButton
        key="filter-row-desktop-button"
        type="primary"
        ssrIcon={FixaFiltersIconPath}
        text={t("filterString2", { count: changedFilterCount })}
        size="small"
        onClick={() => {
          trackerHelper.filter.trackFilterGeneralClicked(
            tracking.feature,
            tracking.mode,
            "open_modal_button"
          );
          onTriggerFilter("");
        }}
      />
      {filters
        .filter(
          ({ enabled, rowVisibility }) =>
            enabled && rowVisibility !== RowVisibility.Hidden
        )
        // Put a single grouped filter in the main filter list
        .map((filter) => {
          if (filter.inputType === InputType.Group) {
            const enabledGroupFilters = filter.filters.filter(
              ({ enabled }) => enabled
            );
            if (enabledGroupFilters.length === 1) {
              return enabledGroupFilters[0]!;
            }
          }
          return filter;
        })
        .map((filter) => {
          const showDropdown =
            !filter.rowVisibility ||
            filter.rowVisibility === RowVisibility.Dropdown;
          switch (filter.inputType) {
            case InputType.Select: {
              if (showDropdown) {
                return (
                  <InputSelectPillDesktop
                    key={filter.id}
                    filter={filter}
                    filterValues={filterValues}
                    setFilterValues={setFilterValues}
                    onTriggerFilter={onTriggerFilter}
                    tracking={tracking}
                  />
                );
              } else {
                return (
                  <InputSelectPillMobile
                    key={filter.id}
                    filter={filter}
                    filterValues={filterValues}
                    onTriggerFilter={onTriggerFilter}
                    tracking={tracking}
                  />
                );
              }
            }
            case InputType.MultiSelect: {
              if (showDropdown) {
                return (
                  <InputMultiSelectPillDesktop
                    key={filter.id}
                    filter={filter}
                    filterValues={filterValues}
                    setFilterValues={setFilterValues}
                    tracking={tracking}
                  />
                );
              } else {
                return (
                  <InputMultiSelectPillMobile
                    key={filter.id}
                    filter={filter}
                    filterValues={filterValues}
                    onTriggerFilter={onTriggerFilter}
                    tracking={tracking}
                  />
                );
              }
            }
            case InputType.Checkbox: {
              return (
                <PillCheckbox
                  key={filter.id}
                  filter={filter}
                  filterValues={filterValues}
                  setFilterValues={setFilterValues}
                  tracking={tracking}
                />
              );
            }
            case InputType.Group:
              if (showDropdown) {
                return (
                  <InputGroupPillDesktop
                    key={filter.id}
                    filter={filter}
                    filterValues={filterValues}
                    setFilterValues={setFilterValues}
                    tracking={tracking}
                  />
                );
              } else {
                return (
                  <InputGroupPillMobile
                    key={filter.id}
                    filter={filter}
                    filterValues={filterValues}
                    onTriggerFilter={onTriggerFilter}
                    tracking={tracking}
                  />
                );
              }
            case InputType.RadioButton: {
              return (
                <PillRadioButton
                  key={filter.id}
                  filter={filter}
                  filterValues={filterValues}
                  setFilterValues={setFilterValues}
                  tracking={tracking}
                />
              );
            }
            default:
              return null;
          }
        })}
    </FilterRowScrollPanel>
  );
};
